/**
 * LocalPListPage — danh sách Local Purchase contracts.
 *
 * UX/UI ĐỒNG BỘ với module Sale Contract (xem ContractListView.tsx):
 *   - TableVirtuoso (react-virtuoso) để virtual-scroll list lớn
 *   - Filter Popover trên header (Search / Vendor / Contract Type / Futures Month / Status / Date)
 *   - Toolbar action buttons ở top-right (New / View / Edit / Copy / Force-close / Delete / Print)
 *   - TablePagination cổ điển với option All=1M
 *   - Layout `Box className="app-dataframe"` cùng spacing với Sales
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Menu,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCopyLocalPMutation,
  useDeleteLocalPMutation,
  useForceCloseLocalPMutation,
  useGetLocalPQuery,
  useLazyPrintLocalPQuery,
  useListLocalPQuery,
} from '@/store/api/localPApi'
import type {
  LocalPContractType,
  LocalPListItem,
  LocalPPrintKind,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import LocalPFormDialog from './LocalPFormDialog'

type FilterKind =
  | 'search'
  | 'vendor'
  | 'contractType'
  | 'futuresMonth'
  | 'status'
  | 'date'

/** TablePagination «All» maps to this pageSize (single request; real row count ≤ total). */
const ALL_ROWS_PAGE_SIZE = 1_000_000

const PRINT_KINDS: Array<{ kind: LocalPPrintKind; label: string }> = [
  { kind: 'contract', label: 'Hợp đồng' },
  { kind: 'contract-forward', label: 'Hợp đồng Forward' },
  { kind: 'cancel', label: 'Phiếu huỷ' },
  { kind: 'dispatch', label: 'Phiếu giao' },
]

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.24),
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },
  },
})

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'number'
type ColumnDef = {
  key: keyof LocalPListItem | '_price' | '_status'
  label: string
  kind?: ColumnKind
  align?: 'right'
}

/** Cột list LocalP — thứ tự + caption từ source DocConvert §LocalP. */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contract_no', label: 'Số HĐ' },
  { key: 'contract_date', label: 'Ngày HĐ', kind: 'date' },
  { key: 'cp_code', label: 'Mã NCC' },
  { key: 'supplier_name', label: 'Nhà cung cấp' },
  { key: 'quantity_kg', label: 'SL (kg)', kind: 'number', align: 'right' },
  { key: 'no_of_bag', label: 'Bao', kind: 'number', align: 'right' },
  { key: 'contract_type', label: 'Loại' },
  { key: '_price', label: 'Giá', kind: 'number', align: 'right' },
  { key: 'futures_month', label: 'Futures' },
  { key: 'latest_delivery', label: 'Latest Delivery', kind: 'date' },
  { key: 'certification', label: 'Cert' },
  { key: 'license_cert_no', label: 'License' },
  { key: 'me_or_ib', label: 'ME/IB' },
  { key: '_status', label: 'Trạng thái' },
  { key: 'created_date', label: 'Ngày tạo', kind: 'date' },
  { key: 'created_by', label: 'Người tạo' },
]

const NON_SUM_TOTAL_KEYS = new Set<string>([])

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const distinctValues = (rows: LocalPListItem[], key: keyof LocalPListItem): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const value = row[key]
    if (value == null) continue
    const text = String(value).trim()
    if (text) values.add(text)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const sumNumericColumn = (rows: LocalPListItem[], key: keyof LocalPListItem): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

const rowMatchesSearch = (row: LocalPListItem, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return TABLE_COLUMNS.some((col) => {
    if (col.key === '_price' || col.key === '_status') return false
    const value = row[col.key as keyof LocalPListItem]
    if (value == null) return false
    return String(value).toLowerCase().includes(needle)
  })
}

const statusText = (row: LocalPListItem): string => {
  if (row.is_status === 0) return 'Cancelled'
  if (row.is_forced_close) return 'Forced-close'
  return 'Active'
}

const cellValue = (row: LocalPListItem, col: ColumnDef): string => {
  if (col.key === '_price') {
    if (row.contract_type === 'Outright')
      return row.outright_price != null ? formatAccessNumber(row.outright_price) : ''
    if (row.contract_type === 'Differential')
      return row.ptbf_diffs != null ? `Diff ${formatAccessNumber(row.ptbf_diffs)}` : ''
    return ''
  }
  if (col.key === '_status') return statusText(row)

  const val = row[col.key as keyof LocalPListItem]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return formatAccessNumber(val)
  return String(val)
}

export function LocalPListPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const navigate = useNavigate()

  // ── Selection ────────────────────────────────────────────────────────────
  const [selectedContractNo, setSelectedContractNo] = useState<string | null>(null)

  // ── Pagination ───────────────────────────────────────────────────────────
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1) // -1 = All

  // ── Filters ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('')
  const [vendorFilter, setVendorFilter] = useState('')
  const [contractTypeFilter, setContractTypeFilter] = useState<LocalPContractType | ''>('')
  const [futuresMonthFilter, setFuturesMonthFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<'' | 'Active' | 'Cancelled' | 'Forced-close'>('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) =>
    setFilterPopover({ kind, anchor: e.currentTarget })

  const hasActiveFilters = Boolean(
    searchTerm.trim() || vendorFilter || contractTypeFilter || futuresMonthFilter || statusFilter || dateFrom || dateTo,
  )
  const clearAllFilters = () => {
    setSearchTerm('')
    setVendorFilter('')
    setContractTypeFilter('')
    setFuturesMonthFilter('')
    setStatusFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(0)
  }

  // ── Dialog state ─────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false)
  const [editingContractNo, setEditingContractNo] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<LocalPListItem | null>(null)
  const [confirmForceClose, setConfirmForceClose] = useState<LocalPListItem | null>(null)
  const [printAnchor, setPrintAnchor] = useState<{ el: HTMLElement; contractNo: string } | null>(null)

  // ── Data fetch (single large request — local filter & paginate) ──────────
  const { data, isLoading, isFetching, error } = useListLocalPQuery({
    page: 1,
    page_size: ALL_ROWS_PAGE_SIZE,
    is_status: undefined,
  })

  const { data: editingDetail } = useGetLocalPQuery(editingContractNo ?? '', {
    skip: !editingContractNo,
  })

  const [deleteLocalP, { isLoading: deleting }] = useDeleteLocalPMutation()
  const [copyLocalP] = useCopyLocalPMutation()
  const [forceClose, { isLoading: forcingClose }] = useForceCloseLocalPMutation()
  const [triggerPrint] = useLazyPrintLocalPQuery()

  const baseRows = useMemo(() => data?.items ?? [], [data])

  // ── Local filter + pagination ────────────────────────────────────────────
  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm)) return false
        if (vendorFilter && row.cp_code !== vendorFilter) return false
        if (contractTypeFilter && row.contract_type !== contractTypeFilter) return false
        if (futuresMonthFilter && row.futures_month !== futuresMonthFilter) return false
        if (statusFilter && statusText(row) !== statusFilter) return false
        if (dateFrom && (!row.contract_date || row.contract_date < dateFrom)) return false
        if (dateTo && (!row.contract_date || row.contract_date > dateTo)) return false
        return true
      }),
    [baseRows, searchTerm, vendorFilter, contractTypeFilter, futuresMonthFilter, statusFilter, dateFrom, dateTo],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = page * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])

  const totalRowValues = useMemo(
    () =>
      new Map(
        TABLE_COLUMNS.filter(
          (col) => col.kind === 'number' && col.key !== '_price' && col.key !== '_status' && !NON_SUM_TOTAL_KEYS.has(String(col.key)),
        ).map((col) => [String(col.key), sumNumericColumn(filteredRows, col.key as keyof LocalPListItem)]),
      ),
    [filteredRows],
  )

  // Distinct option lists derived from baseRows (giống ContractListView)
  const vendorOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const r of baseRows) {
      if (!r.cp_code) continue
      if (!seen.has(r.cp_code)) seen.set(r.cp_code, `${r.cp_code}${r.supplier_name ? ` — ${r.supplier_name}` : ''}`)
    }
    return [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [baseRows])
  const contractTypeOptions = useMemo(() => distinctValues(baseRows, 'contract_type'), [baseRows])
  const futuresMonthOptions = useMemo(() => distinctValues(baseRows, 'futures_month'), [baseRows])
  const statusOptions = ['Active', 'Cancelled', 'Forced-close']

  const idRangeText = useMemo(() => {
    if (rows.length === 0) return 'HĐ: —'
    return `HĐ: ${rows[0].contract_no}–${rows[rows.length - 1].contract_no}`
  }, [rows])

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`

  const selectedRow = useMemo<LocalPListItem | null>(
    () => baseRows.find((r) => r.contract_no === selectedContractNo) ?? null,
    [baseRows, selectedContractNo],
  )

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleView = () => {
    if (!selectedRow) return
    navigate(`/purchase/local-p/${encodeURIComponent(selectedRow.contract_no)}`)
  }
  const handleEdit = () => {
    if (!selectedRow) return
    setEditingContractNo(selectedRow.contract_no)
  }
  const handleCopy = async () => {
    if (!selectedRow) return
    try {
      const res = await copyLocalP(selectedRow.contract_no).unwrap()
      snack.success(res.message ?? `Đã copy thành ${res.data?.contract_no ?? ''}`)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Copy hợp đồng thất bại'))
    }
  }
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteLocalP(confirmDelete.contract_no).unwrap()
      snack.success(`Đã xoá hợp đồng ${confirmDelete.contract_no}`)
      setConfirmDelete(null)
      if (selectedContractNo === confirmDelete.contract_no) setSelectedContractNo(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Xoá hợp đồng thất bại'))
    }
  }
  const handleConfirmForceClose = async () => {
    if (!confirmForceClose) return
    try {
      await forceClose({ contractNo: confirmForceClose.contract_no }).unwrap()
      snack.success(`Đã force-close ${confirmForceClose.contract_no}`)
      setConfirmForceClose(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Force-close thất bại'))
    }
  }
  const handlePrint = async (kind: LocalPPrintKind) => {
    if (!printAnchor) return
    const contractNo = printAnchor.contractNo
    setPrintAnchor(null)
    try {
      await triggerPrint({ contractNo, kind }).unwrap()
      snack.info(`Yêu cầu in (${kind}) đã gửi`)
    } catch (err) {
      snack.error(getErrorMessage(err, 'In PDF thất bại'))
    }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action + filter bar */}
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
            Filter
          </Typography>
          <Button
            size="small"
            variant={searchTerm.trim() ? 'contained' : 'outlined'}
            color={searchTerm.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('search', e)}
            sx={filterBarBtnSx}
          >
            Search
          </Button>
          <Button
            size="small"
            variant={vendorFilter ? 'contained' : 'outlined'}
            color={vendorFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('vendor', e)}
            sx={filterBarBtnSx}
          >
            Vendor
          </Button>
          <Button
            size="small"
            variant={contractTypeFilter ? 'contained' : 'outlined'}
            color={contractTypeFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('contractType', e)}
            sx={filterBarBtnSx}
          >
            Contract Type
          </Button>
          <Button
            size="small"
            variant={futuresMonthFilter ? 'contained' : 'outlined'}
            color={futuresMonthFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('futuresMonth', e)}
            sx={filterBarBtnSx}
          >
            Futures Month
          </Button>
          <Button
            size="small"
            variant={statusFilter ? 'contained' : 'outlined'}
            color={statusFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('status', e)}
            sx={filterBarBtnSx}
          >
            Status
          </Button>
          <Button
            size="small"
            variant={dateFrom || dateTo ? 'contained' : 'outlined'}
            color={dateFrom || dateTo ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('date', e)}
            sx={filterBarBtnSx}
          >
            Date
          </Button>
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button
            variant="contained"
            size="small"
            color="success"
            sx={actionToolbarBtnSx}
            disabled={!canEdit}
            onClick={() => setCreateOpen(true)}
          >
            New
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow}
            onClick={handleView}
          >
            View
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || !canEdit}
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || !canEdit}
            onClick={() => void handleCopy()}
          >
            Copy
          </Button>
          <Tooltip title={selectedRow?.is_forced_close ? 'Đã force-close' : ''}>
            <span>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                sx={actionToolbarBtnSx}
                disabled={!selectedRow || !!selectedRow?.is_forced_close || !canEdit}
                onClick={() => selectedRow && setConfirmForceClose(selectedRow)}
              >
                Force-close
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            color="error"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || !canEdit}
            onClick={() => selectedRow && setConfirmDelete(selectedRow)}
          >
            Delete
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
            disabled={!selectedRow}
            onClick={(e) =>
              selectedRow && setPrintAnchor({ el: e.currentTarget, contractNo: selectedRow.contract_no })
            }
          >
            Print
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Local Purchase contracts.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        {/* Pagination toolbar */}
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
            minHeight: 30,
            py: 0.125,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Loaded: {baseRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
                '& .MuiTablePagination-select': { fontSize: '0.72rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.92)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>

        {/* Filter popovers */}
        <Popover
          open={filterPopover !== null}
          anchorEl={filterPopover?.anchor ?? null}
          onClose={closeFilterPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 360 } }}
        >
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                label="Tìm kiếm"
                placeholder="Số HĐ, vendor, ref..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }}
              />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>
                Clear Search
              </Button>
            </Stack>
          )}
          {filterPopover?.kind === 'vendor' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select
                label="Vendor"
                value={vendorFilter}
                onChange={(e) => { setVendorFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {vendorOptions.map(([code, label]) => (
                  <MenuItem key={code} value={code}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'contractType' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Contract Type</InputLabel>
              <Select
                label="Contract Type"
                value={contractTypeFilter}
                onChange={(e) => { setContractTypeFilter(e.target.value as LocalPContractType | ''); setPage(0); closeFilterPopover() }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {contractTypeOptions.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'futuresMonth' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Futures Month</InputLabel>
              <Select
                label="Futures Month"
                value={futuresMonthFilter}
                onChange={(e) => { setFuturesMonthFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {futuresMonthOptions.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'status' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(0); closeFilterPopover() }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {statusOptions.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'date' && (
            <Stack spacing={1} sx={{ minWidth: 240 }}>
              <DatePicker
                label="Từ ngày"
                format="DD-MMM-YYYY"
                value={dateFrom ? dayjs(dateFrom) : null}
                onChange={(d) => { setDateFrom(d ? d.format('YYYY-MM-DD') : ''); setPage(0) }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="Đến ngày"
                format="DD-MMM-YYYY"
                value={dateTo ? dayjs(dateTo) : null}
                onChange={(d) => { setDateTo(d ? d.format('YYYY-MM-DD') : ''); setPage(0) }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <Button size="small" variant="text" onClick={() => { setDateFrom(''); setDateTo(''); setPage(0) }}>
                Clear Date
              </Button>
            </Stack>
          )}
        </Popover>

        {/* Virtualized table */}
        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell
                    key={String(col.key)}
                    align={col.align}
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col, index) => {
                  const total = totalRowValues.get(String(col.key))
                  const val = total == null ? '' : formatAccessNumber(total)
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: LocalPListItem) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={String(col.key)} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>
                      {val || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 2000, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const rowContractNo = Number.isFinite(idx) ? rows[idx]?.contract_no : null
                return (
                  <TableRow
                    {...props}
                    hover
                    selected={rowContractNo != null && rowContractNo === selectedContractNo}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (rowContractNo != null) setSelectedContractNo(rowContractNo)
                    }}
                  />
                )
              },
            }}
          />
        </Box>
      </Box>

      {/* Dialogs + menus */}
      <LocalPFormDialog
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
      />
      <LocalPFormDialog
        open={!!editingContractNo && !!editingDetail?.data}
        mode="edit"
        initialValue={editingDetail?.data ?? null}
        onClose={() => setEditingContractNo(null)}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Xoá hợp đồng"
        message={confirmDelete ? `Bạn chắc chắn muốn xoá hợp đồng ${confirmDelete.contract_no}?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      <ConfirmDialog
        open={!!confirmForceClose}
        title="Force-close hợp đồng"
        message={
          confirmForceClose
            ? `Bạn chắc chắn muốn Force-close hợp đồng ${confirmForceClose.contract_no}? Thao tác này thường không thể khôi phục.`
            : ''
        }
        destructive
        loading={forcingClose}
        onConfirm={() => void handleConfirmForceClose()}
        onCancel={() => setConfirmForceClose(null)}
      />
      <Menu
        anchorEl={printAnchor?.el ?? null}
        open={!!printAnchor}
        onClose={() => setPrintAnchor(null)}
      >
        {PRINT_KINDS.map((k) => (
          <MenuItem key={k.kind} onClick={() => void handlePrint(k.kind)}>
            {k.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default LocalPListPage
